import { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Trash2,
  RotateCcw,
  AlertTriangle,
  Download,
  Loader2,
  XCircle,
} from "lucide-react";

const TRASH_FILTER_FIELDS: FilterField[] = [
  {
    key: "module",
    label: "Module",
    type: "combobox-multi",
    options: [
      { value: "Customer", label: "Khách hàng" },
      { value: "Product", label: "Sản phẩm" },
      { value: "Order", label: "Đơn hàng" },
      { value: "User", label: "Người dùng" },
    ],
  },
  { key: "recordId", label: "ID Bản ghi", type: "text", placeholder: "ID..." },
  { key: "originalData", label: "Dữ liệu gốc", type: "text", placeholder: "Tìm nội dung..." },
  { key: "deletedBy", label: "Người xóa", type: "text", placeholder: "Tên người xóa..." },
  { key: "deletedAt", label: "Thời gian", type: "text", placeholder: "dd/mm/yyyy..." },
];

import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { trpcClient } from "@/lib/trpc";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import { SortableHeader } from "@/components/shared/SortableHeader";
import { InlineFilterRow } from "@/components/shared/InlineFilterRow";
import { DataTableRow, DataTableRowCheckbox, DataTableSelectAllCheckbox } from "@/components/shared/DataTableRow";
import { useRowSelection } from "@/hooks/useRowSelection";
import { BulkActionBar, type BulkAction } from "@/components/shared/BulkActionBar";
import { useConfirm } from "@/providers/ConfirmProvider";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { exportToExcel } from "@/lib/excel.utils";
import type { FilterField } from "@/components/shared/FilterBar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";

export default function TrashList() {
  const queryClient = useQueryClient();
  const { hasPermission } = useRoleAccess();

  // Dialogs
  const [restoreItem, setRestoreItem] = useState<any>(null);
  const [hardDeleteItem, setHardDeleteItem] = useState<any>(null);
  const [viewJsonItem, setViewJsonItem] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Permissions
  const canManageTrash = hasPermission("trash", "view") || true;

  // Server-side filter
  const {
    items,
    isLoading,
    currentPage,
    pageSize,
    totalItems,
    sortField,
    sortOrder,
    handleSort,
    onPageChange,
    onPageSizeChange,
    onGroupChange,
    groupFilter,
    showFilter,
    onToggleFilter,
    filterValues,
    onFilterChange,
  } = useServerListFilter<any>({
    queryKey: ["trash"],
    queryFn: (params) => {
      return trpcClient.trash.getTrashList.query({
        page: params.page,
        limit: params.limit,
        search: params.search,
        module:
          params.module && params.module !== "_none"
            ? params.module
            : undefined,
      });
    },
    groupParamName: "module",
  });

  const displayItems = useMemo(() => {
    return items.filter((item: any) => {
      if (filterValues.module && filterValues.module !== "_none") {
        if (item.module?.toLowerCase() !== String(filterValues.module).toLowerCase()) return false;
      }
      if (filterValues.recordId) {
        if (!item.recordId?.toLowerCase().includes(String(filterValues.recordId).toLowerCase())) return false;
      }
      if (filterValues.originalData) {
        const rawJson = JSON.stringify(item.originalData || "").toLowerCase();
        if (!rawJson.includes(String(filterValues.originalData).toLowerCase())) return false;
      }
      if (filterValues.deletedBy) {
        const name = (item.user?.fullName || "").toLowerCase();
        if (!name.includes(String(filterValues.deletedBy).toLowerCase())) return false;
      }
      if (filterValues.deletedAt) {
        const dStr = item.deletedAt ? format(new Date(item.deletedAt), "dd/MM/yyyy") : "";
        if (!dStr.includes(String(filterValues.deletedAt))) return false;
      }
      return true;
    });
  }, [items, filterValues]);

  const confirm = useConfirm();
  const rowIds = useMemo(() => displayItems.map((i: any) => i.id), [displayItems]);
  const rowSelection = useRowSelection(rowIds);
  const { selectedCount, selectedIds, clearSelection } = rowSelection;
  const selectedItems = () => displayItems.filter((i: any) => selectedIds.has(i.id));

  const exportTrash = (rows: any[]) =>
    exportToExcel(
      rows.map((i: any) => ({
        module: i.module,
        recordId: i.recordId,
        originalData: JSON.stringify(i.originalData ?? ""),
        deletedBy: i.user?.fullName || "Hệ thống",
        deletedAt: i.deletedAt ? format(new Date(i.deletedAt), "dd/MM/yyyy HH:mm") : "",
      })),
      [
        { key: "module", label: "Module", width: 16 },
        { key: "recordId", label: "ID bản ghi", width: 38 },
        { key: "originalData", label: "Dữ liệu gốc", width: 60 },
        { key: "deletedBy", label: "Người xoá", width: 22 },
        { key: "deletedAt", label: "Thời gian", width: 18 },
      ],
      "thung_rac",
    );

  const bulkActions: BulkAction[] = [
    {
      icon: <Download className="h-4 w-4" />,
      label: `Xuất Excel ${selectedCount} dòng`,
      onClick: () => exportTrash(selectedItems()),
    },
    {
      icon: <RotateCcw className="h-4 w-4" />,
      label: "Khôi phục",
      onClick: () =>
        executeBulkAction({
          confirmFn: confirm,
          selectedIds: selectedItems(),
          actionFn: async (item: any) =>
            item.batchId
              ? trpcClient.trash.restoreBatch.mutate({ batchId: item.batchId })
              : trpcClient.trash.restore.mutate({ id: item.id }),
          actionName: "khôi phục",
          itemType: "bản ghi",
          requireConfirm: true,
          confirmMessage: `Khôi phục ${selectedCount} bản ghi đã chọn?`,
          onSuccess: () => {
            clearSelection();
            queryClient.invalidateQueries();
          },
        }),
    },
  ];
  if (hasPermission("trash", "delete")) {
    bulkActions.push({
      icon: <XCircle className="h-4 w-4" />,
      label: "Xoá vĩnh viễn",
      variant: "destructive",
      onClick: () =>
        executeBulkAction({
          confirmFn: confirm,
          selectedIds: Array.from(selectedIds),
          actionFn: async (id: string) => trpcClient.trash.hardDelete.mutate({ id }),
          actionName: "xoá vĩnh viễn",
          itemType: "bản ghi",
          requireConfirm: true,
          confirmMessage: `Xoá vĩnh viễn ${selectedCount} bản ghi đã chọn? Không thể khôi phục lại.`,
          onSuccess: () => {
            clearSelection();
            queryClient.invalidateQueries({ queryKey: ["trash"] });
          },
        }),
    });
  }

  const MODULES = [
    { id: "Customer", label: "Khách hàng" },
    { id: "Product", label: "Sản phẩm" },
    { id: "Order", label: "Đơn hàng" },
    { id: "User", label: "Người dùng" },
  ];

  const handleRestore = async () => {
    if (!restoreItem) return;
    setIsProcessing(true);
    try {
      if (restoreItem.batchId) {
        await trpcClient.trash.restoreBatch.mutate({ batchId: restoreItem.batchId });
      } else {
        await trpcClient.trash.restore.mutate({ id: restoreItem.id });
      }
      toast.success(`Đã khôi phục dữ liệu từ module ${restoreItem.module}`);
      queryClient.invalidateQueries({ queryKey: ["trash"] });

      const moduleName = restoreItem.module.toLowerCase() + "s";
      queryClient.invalidateQueries({ queryKey: [moduleName] });
    } catch (error: any) {
      toast.error(error.message || "Lỗi khi khôi phục dữ liệu");
    } finally {
      setIsProcessing(false);
      setRestoreItem(null);
    }
  };

  const handleHardDelete = async () => {
    if (!hardDeleteItem) return;
    setIsProcessing(true);
    try {
      await trpcClient.trash.hardDelete.mutate({ id: hardDeleteItem.id });
      toast.success(`Đã xoá vĩnh viễn dữ liệu khỏi hệ thống`);
      queryClient.invalidateQueries({ queryKey: ["trash"] });
    } catch (error: any) {
      toast.error(error.message || "Lỗi khi xoá dữ liệu");
    } finally {
      setIsProcessing(false);
      setHardDeleteItem(null);
    }
  };

  if (!canManageTrash) {
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4 text-slate-500 mt-20">
        <AlertTriangle className="h-12 w-12 text-rose-500" />
        <h2 className="text-xl font-semibold">Từ chối truy cập</h2>
        <p>Bạn không có quyền xem hoặc quản lý Thùng rác hệ thống.</p>
      </div>
    );
  }

  return (
    <ListPageShell
      unitLabel="bản ghi"
      currentPage={currentPage}
      pageSize={pageSize}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      showFilter={showFilter}
      onToggleFilter={onToggleFilter}
      onClearAll={() => onFilterChange({})}
      filterFields={TRASH_FILTER_FIELDS}
      filterValues={filterValues}
      onExport={() => exportTrash(displayItems)}
      sidebar={
        <div className="w-64 border-r border-slate-200 bg-white flex flex-col hidden md:flex">
          <div className="p-4 border-b border-slate-100 bg-slate-50/50">
            <div className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <Trash2 className="h-4 w-4 text-rose-500" />
              Danh mục
            </div>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1">
              <button
                onClick={() => onGroupChange(null)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  !groupFilter
                    ? "bg-rose-50 text-rose-700"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                Tất cả
              </button>
              {MODULES.map((m) => (
                <button
                  key={m.id}
                  onClick={() => onGroupChange(m.id)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    groupFilter === m.id
                      ? "bg-rose-50 text-rose-700"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </ScrollArea>
        </div>
      }
    >
      <Table>
        <TableHeader className="bg-slate-50 sticky top-0 z-10 shadow-sm">
          <TableRow>
            <DataTableSelectAllCheckbox rowSelection={rowSelection} />
            <TableHead className="w-[80px] text-center">STT</TableHead>
            <SortableHeader
              label="Module"
              field="module"
              currentField={sortField}
              currentOrder={sortOrder}
              onSort={handleSort}
              className="w-[150px]"
            />
            <SortableHeader
              label="ID Bản ghi"
              field="recordId"
              currentField={sortField}
              currentOrder={sortOrder}
              onSort={handleSort}
              className="apv-col-code w-[200px]"
            />
            <TableHead className="min-w-[200px]">Dữ liệu gốc</TableHead>
            <SortableHeader
              label="Người xoá"
              field="user.fullName"
              currentField={sortField}
              currentOrder={sortOrder}
              onSort={handleSort}
              className="w-[200px]"
            />
            <SortableHeader
              label="Thời gian"
              field="deletedAt"
              currentField={sortField}
              currentOrder={sortOrder}
              onSort={handleSort}
              className="w-[150px]"
            />
            <TableHead className="apv-col-actions w-[120px] text-center">Thao tác</TableHead>
          </TableRow>
          {showFilter && (
            <InlineFilterRow
              columns={[
                null,
                null,
                { key: "module" },
                { key: "recordId" },
                { key: "originalData" },
                { key: "deletedBy" },
                { key: "deletedAt" },
                null,
              ]}
              filterFields={TRASH_FILTER_FIELDS}
              filterValues={filterValues}
              onFilterChange={onFilterChange}
            />
          )}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={8} className="h-24 text-center">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-rose-500" />
              </TableCell>
            </TableRow>
          ) : displayItems.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={8}
                className="h-32 text-center text-slate-500"
              >
                <Trash2 className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                Thùng rác trống
              </TableCell>
            </TableRow>
          ) : (
            displayItems.map((item: any, idx: number) => {
              const rawJson = JSON.stringify(item.originalData);
              const preview =
                rawJson.length > 50
                  ? rawJson.substring(0, 50) + "..."
                  : rawJson;
              return (
                <DataTableRow
                  key={item.id}
                  id={item.id}
                  rowSelection={rowSelection}
                  className="group hover:bg-slate-50/70 transition-colors"
                  onClick={() => setViewJsonItem(item)}
                >
                  <DataTableRowCheckbox id={item.id} rowSelection={rowSelection} />
                  <TableCell className="text-center text-sm text-slate-500">
                    {(currentPage - 1) * pageSize + idx + 1}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-slate-50">
                      {item.module}
                    </Badge>
                  </TableCell>
                  <TableCell className="apv-col-code">
                    <div className="font-mono text-xs text-slate-600 truncate bg-slate-50 px-2 py-1 rounded">
                      {item.recordId}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <span className="truncate mr-2" title={rawJson}>
                        {preview}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-slate-700">
                        {item.user?.fullName || "Hệ thống"}
                      </span>
                      <span className="text-xs text-slate-400">
                        {item.user?.employeeCode || ""}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {format(new Date(item.deletedAt), "dd/MM/yyyy HH:mm")}
                  </TableCell>
                  <TableCell
                    className="apv-col-actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                        onClick={() => setRestoreItem(item)}
                        title="Khôi phục"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-rose-500 hover:text-rose-600 hover:bg-rose-50"
                        onClick={() => setHardDeleteItem(item)}
                        title="Xóa vĩnh viễn"
                      >
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </DataTableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* View JSON Dialog */}
      <Dialog
        open={!!viewJsonItem}
        onOpenChange={(open) => !open && setViewJsonItem(null)}
      >
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Dữ liệu JSON Gốc - {viewJsonItem?.module}</DialogTitle>
            <DialogDescription>
              ID Bản ghi:{" "}
              <span className="font-mono bg-slate-100 px-1 py-0.5 rounded text-xs">
                {viewJsonItem?.recordId}
              </span>
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 bg-slate-950 p-4 rounded-md mt-4">
            <pre className="text-emerald-400 font-mono text-sm whitespace-pre-wrap">
              {viewJsonItem &&
                JSON.stringify(viewJsonItem.originalData, null, 2)}
            </pre>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Restore Alert */}
      <AlertDialog
        open={!!restoreItem}
        onOpenChange={(open) => !open && setRestoreItem(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xác nhận khôi phục?</AlertDialogTitle>
            <AlertDialogDescription>
              Bạn có chắc chắn muốn khôi phục bản ghi{" "}
              <strong>{restoreItem?.recordId}</strong> của module{" "}
              <strong>{restoreItem?.module}</strong> không? Dữ liệu sẽ được phục
              hồi lại vào bảng chính và biến mất khỏi thùng rác.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isProcessing}>Huỷ</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestore}
              disabled={isProcessing}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {isProcessing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RotateCcw className="h-4 w-4 mr-2" />
              )}
              Khôi phục
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hard Delete Alert */}
      <AlertDialog
        open={!!hardDeleteItem}
        onOpenChange={(open) => !open && setHardDeleteItem(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-rose-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Xoá vĩnh viễn?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Hành động này sẽ{" "}
              <strong className="text-rose-600">XOÁ VĨNH VIỄN</strong> bản ghi
              này khỏi hệ thống. Bạn không thể khôi phục lại dữ liệu này sau khi
              đã thực hiện.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isProcessing}>Huỷ</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleHardDelete}
              disabled={isProcessing}
              className="bg-rose-600 hover:bg-rose-700"
            >
              {isProcessing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Xoá Vĩnh Viễn
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={bulkActions}
      />
    </ListPageShell>
  );
}
